import React, { useState, useContext } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { trackMetaEvent } from '../utils/metaPixel';
import { toast } from 'sonner';
import { Wallet, ShieldCheck } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import PasswordInput from '../components/PasswordInput';
import { API, AuthContext } from '../App';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import LegalInfoDialog from '../components/LegalInfoDialog';

const AuthPage = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const { t } = useLanguage();
  // ?mode=register → ouvre directement le formulaire d'inscription
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'register');
  const [step, setStep] = useState('credentials'); // 'credentials' or '2fa'
  const [tempToken, setTempToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [acceptLegal, setAcceptLegal] = useState(false);
  // Consentement Loi 25 (Québec) — case distincte, obligatoire à l'inscription.
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);

  const submitForgotPassword = async () => {
    const target = (forgotEmail || formData.email || '').trim();
    if (!target) { toast.error('Saisissez votre adresse email'); return; }
    setForgotSending(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: target });
      setForgotDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setForgotSending(false);
    }
  };
  // Email de l'utilisateur qui vient de s'inscrire — affiche une bannière
  // persistante avec instructions et avertissement spam.
  const [registrationEmailSent, setRegistrationEmailSent] = useState('');
  const [resendingVerification, setResendingVerification] = useState(false);

  const handleResendVerification = async () => {
    if (!registrationEmailSent) return;
    setResendingVerification(true);
    try {
      await axios.post(`${API}/auth/resend-verification?email=${encodeURIComponent(registrationEmailSent)}`);
      toast.success('Email d\'activation renvoyé. Vérifiez aussi votre dossier spam.');
    } catch {
      toast.error('Erreur lors de l\'envoi. Réessayez dans quelques instants.');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLogin && !acceptLegal) {
      toast.error(t('auth.legalConsentRequired'));
      return;
    }
    if (!isLogin && !acceptPrivacy) {
      toast.error(t('auth.privacyConsentRequired'));
      return;
    }
    setLoading(true);

    try {
      if (isLogin) {
        const params = new URLSearchParams();
        params.append('email', formData.email);
        params.append('password', formData.password);
        
        const response = await axios.post(`${API}/auth/login?${params.toString()}`);
        
        if (response.data.requires_2fa) {
          setTempToken(response.data.temp_token);
          setStep('2fa');
          toast.info(t('auth.twoFactorPrompt'));
        } else {
          login(response.data.access_token);
          toast.success(t('auth.loginSuccess'));
          navigate('/dashboard');
        }
      } else {
        await axios.post(`${API}/auth/register`, {
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          privacy_consent: acceptPrivacy
        });
        // Affiche une bannière persistante côté UI plutôt qu'un toast volatil :
        // le user DOIT savoir qu'un email a été envoyé et qu'il peut être en spam.
        setRegistrationEmailSent(formData.email);
        trackMetaEvent('CompleteRegistration');
        toast.success('Compte créé ! Vérifiez votre email pour l\'activer.');
        setIsLogin(true);
        setFormData({ email: formData.email, password: '', full_name: '' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.error'));
    } finally {
      setLoading(false);
    }
  };

  const verify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login/2fa`, {
        temp_token: tempToken,
        code: twoFactorCode
      });
      login(response.data.access_token);
      toast.success(t('auth.loginSuccess'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code invalide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-100">
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1606189934846-a527add8a77b?auto=format&fit=crop&w=1200&q=80"
          alt={t('auth.heroTitle')}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-blue-900/30"></div>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="text-3xl font-bold mb-2 drop-shadow-lg">{t('auth.heroTitle')}</h2>
          <p className="text-lg drop-shadow-lg opacity-90">{t('auth.heroSubtitle')}</p>
        </div>
      </div>
      
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-100">
        <div className="w-full max-w-md">
          <Link to="/" data-testid="auth-logo-link" className="flex items-center gap-2 mb-8 justify-center hover:opacity-80 transition-opacity">
            <Wallet size={40} weight="duotone" className="text-blue-700" />
            <span className="text-3xl font-bold tracking-tight text-black">Auto-Gère</span>
          </Link>
          
          <div className="mb-4 flex justify-end">
            <LanguageSelector />
          </div>
          
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold tracking-tight text-black mb-2">
              {step === '2fa' ? t('auth.twoFactorTitle') : (isLogin ? t('auth.welcome') : t('auth.createAccount'))}
            </h1>
            <p className="text-slate-700">
              {step === '2fa' ? t('auth.twoFactorSubtitle') : (isLogin ? t('auth.loginSubtitle') : t('auth.registerSubtitle'))}
            </p>
          </div>

          {/* Bannière permanente "Connexion sécurisée" — argument de confiance,
              cohérent avec le positionnement banque/finance d'Auto-Gère.
              Mise en avant de l'authentification par cookie httpOnly v1.02+. */}
          {step !== '2fa' && (
            <div
              data-testid="auth-security-trust-banner"
              className="mb-6 flex items-start gap-3 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white px-4 py-3 shadow-sm"
            >
              <div className="flex-shrink-0 mt-0.5">
                <div className="relative">
                  <ShieldCheck size={28} weight="duotone" className="text-blue-700" />
                  <span className="absolute -bottom-0.5 -right-0.5 inline-flex h-2.5 w-2.5 rounded-full bg-blue-600 ring-2 ring-white" aria-hidden />
                </div>
              </div>
              <div className="text-left flex-1 min-w-0">
                <p className="text-sm font-bold text-blue-900 leading-snug flex items-center gap-1.5 flex-wrap">
                  {t('auth.trustBannerTitle')}
                  <span className="inline-flex items-center rounded-full bg-blue-700 text-white text-[10px] font-extrabold tracking-wider px-1.5 py-0.5">
                    v1.02
                  </span>
                </p>
                <p className="text-xs text-slate-700 leading-relaxed mt-1">
                  {t('auth.trustBannerDesc')}
                </p>
              </div>
            </div>
          )}

          {/* Bannière post-inscription : email d'activation envoyé + avertissement spam */}
          {registrationEmailSent && step !== '2fa' && (
            <div
              data-testid="post-register-banner"
              className="mb-6 bg-blue-50 border-2 border-blue-400 rounded-xl p-4 space-y-2"
            >
              <p className="font-bold text-blue-900 flex items-center gap-2">
                <span aria-hidden>📨</span> {t('auth.activationEmailSent')}
              </p>
              <p className="font-mono text-sm text-blue-800 break-all bg-white border border-blue-200 rounded px-2 py-1">
                {registrationEmailSent}
              </p>
              <div className="bg-orange-50 border-l-4 border-orange-400 rounded p-3 text-xs text-orange-900 leading-relaxed">
                <p className="font-bold mb-1">⚠ {t('auth.activationSpamWarning')}</p>
                <p>{t('auth.activationSpamBody')}</p>
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  data-testid="post-register-resend-btn"
                  onClick={handleResendVerification}
                  disabled={resendingVerification}
                  className="bg-blue-700 text-white hover:bg-blue-800 font-semibold flex-1 text-sm"
                >
                  {resendingVerification ? `${t('auth.loading')}` : t('auth.resendActivation')}
                </Button>
                <Button
                  type="button"
                  data-testid="post-register-dismiss-btn"
                  variant="ghost"
                  onClick={() => setRegistrationEmailSent('')}
                  className="text-slate-700 hover:text-black text-sm"
                >
                  {t('common.close')}
                </Button>
              </div>
            </div>
          )}

          {step === '2fa' ? (
            <form onSubmit={verify2FA} className="space-y-4">
              <div>
                <Label htmlFor="2fa-code" className="text-black">{t('auth.twoFactorCodeLabel')}</Label>
                <Input
                  id="2fa-code"
                  data-testid="login-2fa-input"
                  type="text"
                  maxLength={10}
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  required
                  autoFocus
                  placeholder="123456"
                  className="mt-1 text-center text-2xl tracking-widest font-mono bg-white border-2 border-blue-200 focus:border-blue-600"
                />
                <p className="text-xs text-slate-700 mt-2">
                  {t('auth.twoFactorHint')}
                </p>
              </div>
              <Button
                data-testid="verify-2fa-btn"
                type="submit"
                className="w-full bg-white text-black border-2 border-blue-600 hover:bg-slate-50 hover:border-blue-700 font-semibold"
                disabled={loading || !twoFactorCode}
              >
                {loading ? t('auth.twoFactorVerifying') : t('auth.twoFactorVerify')}
              </Button>
              <button
                type="button"
                onClick={() => {
                  setStep('credentials');
                  setTwoFactorCode('');
                  setTempToken('');
                }}
                className="w-full text-sm text-slate-700 hover:text-blue-700"
              >
                {t('auth.twoFactorBack')}
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="full_name" data-testid="fullname-label" className="text-black">{t('auth.fullName')}</Label>
                <Input
                  id="full_name"
                  data-testid="fullname-input"
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required={!isLogin}
                  className="mt-1 bg-white border-2 border-blue-200 focus:border-blue-600"
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="email" data-testid="email-label" className="text-black">{t('auth.email')}</Label>
              <Input
                id="email"
                name="email"
                autoComplete="email"
                data-testid="email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1 bg-white border-2 border-blue-200 focus:border-blue-600"
              />
            </div>
            
            <div>
              <Label htmlFor="password" data-testid="password-label" className="text-black">{t('auth.password')}</Label>
              <PasswordInput
                id="password"
                testId="password-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-1 bg-white border-2 border-blue-200 focus:border-blue-600"
              />
              {isLogin && (
                <div className="text-right mt-1">
                  <button
                    type="button"
                    data-testid="forgot-password-link"
                    onClick={() => setForgotOpen(true)}
                    className="text-xs text-blue-700 hover:text-blue-900 hover:underline font-semibold"
                  >
                    {t('auth.forgotPassword')}
                  </button>
                </div>
              )}
            </div>
            
            {!isLogin && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 space-y-3">
                <div className="flex items-start gap-3">
                  <input
                    id="accept-legal"
                    data-testid="auth-accept-legal-checkbox"
                    type="checkbox"
                    checked={acceptLegal}
                    onChange={(e) => setAcceptLegal(e.target.checked)}
                    required
                    className="mt-1 w-5 h-5 accent-blue-600 shrink-0 cursor-pointer"
                  />
                  <Label htmlFor="accept-legal" className="text-sm text-slate-800 leading-snug cursor-pointer">
                    {t('auth.legalConsentLabel')}{' '}
                    <button
                      type="button"
                      data-testid="auth-open-legal-btn"
                      onClick={() => setLegalOpen(true)}
                      className="text-blue-700 hover:text-blue-900 hover:underline font-semibold"
                    >
                      {t('auth.legalConsentLink')}
                    </button>
                    .
                  </Label>
                </div>
                {/* Consentement Loi 25 — case distincte et obligatoire */}
                <div className="flex items-start gap-3 pt-3 border-t border-blue-200">
                  <input
                    id="accept-privacy"
                    data-testid="auth-accept-privacy-checkbox"
                    type="checkbox"
                    checked={acceptPrivacy}
                    onChange={(e) => setAcceptPrivacy(e.target.checked)}
                    required
                    className="mt-1 w-5 h-5 accent-blue-600 shrink-0 cursor-pointer"
                  />
                  <Label htmlFor="accept-privacy" className="text-sm text-slate-800 leading-snug cursor-pointer">
                    {t('auth.privacyConsentLabel')}{' '}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="auth-open-privacy-link"
                      className="text-blue-700 hover:text-blue-900 hover:underline font-semibold"
                    >
                      {t('auth.privacyConsentLink')}
                    </a>
                    {' '}<span className="text-slate-600">({t('auth.privacyConsentLaw')})</span>.
                  </Label>
                </div>
              </div>
            )}

            <Button
              data-testid="auth-submit-btn"
              type="submit"
              className="w-full bg-white text-black border-2 border-blue-600 hover:bg-slate-50 hover:border-blue-700 font-semibold"
              disabled={loading || (!isLogin && (!acceptLegal || !acceptPrivacy))}
            >
              {loading ? t('auth.loading') : (isLogin ? t('auth.loginButton') : t('auth.registerButton'))}
            </Button>
          </form>
          )}

          {/* Dialogue légal contrôlé (s'ouvre au clic sur le lien) */}
          <LegalInfoDialog open={legalOpen} onOpenChange={setLegalOpen} hideTrigger />

          {/* ============ Dialogue "Mot de passe oublié" ============ */}
          <Dialog
            open={forgotOpen}
            onOpenChange={(open) => {
              setForgotOpen(open);
              if (!open) {
                // Reset à la fermeture pour permettre une nouvelle demande
                setForgotEmail('');
                setForgotDone(false);
              }
            }}
          >
            <DialogContent
              data-testid="forgot-password-dialog"
              className="bg-white border-2 border-blue-300 max-w-md"
            >
              <DialogHeader>
                <DialogTitle className="text-black text-xl">{t('auth.forgotPasswordTitle')}</DialogTitle>
                <DialogDescription className="text-slate-700">
                  {forgotDone
                    ? t('auth.forgotSuccess')
                    : t('auth.forgotPasswordDesc')}
                </DialogDescription>
              </DialogHeader>

              {!forgotDone ? (
                <form
                  onSubmit={(e) => { e.preventDefault(); submitForgotPassword(); }}
                  className="space-y-4 pt-2"
                >
                  <div>
                    <Label htmlFor="forgot-email" className="text-black">{t('auth.forgotEmailLabel')}</Label>
                    <Input
                      id="forgot-email"
                      data-testid="forgot-password-email-input"
                      type="email"
                      required
                      autoComplete="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder={t('auth.emailPlaceholder')}
                      className="mt-1 bg-white border-2 border-blue-200 focus:border-blue-600"
                    />
                  </div>
                  <DialogFooter className="gap-2 sm:gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      data-testid="forgot-password-cancel-btn"
                      onClick={() => setForgotOpen(false)}
                      className="text-slate-700"
                    >
                      {t('auth.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      data-testid="forgot-password-submit-btn"
                      disabled={forgotSending || !forgotEmail.trim()}
                      className="bg-blue-700 text-white hover:bg-blue-800 font-semibold disabled:opacity-60"
                    >
                      {forgotSending ? t('auth.loading') : t('auth.forgotSubmit')}
                    </Button>
                  </DialogFooter>
                  <p className="text-xs text-slate-500 text-center">
                    {t('auth.forgotRateLimit')}
                  </p>
                </form>
              ) : (
                <div className="space-y-4 pt-2">
                  <div
                    data-testid="forgot-password-done-banner"
                    className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 text-sm text-blue-900"
                  >
                    <p className="font-bold mb-1">{t('auth.forgotSentTitle')}</p>
                    <p>{t('auth.forgotSentBody')}</p>
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      data-testid="forgot-password-close-btn"
                      onClick={() => setForgotOpen(false)}
                      className="bg-blue-700 text-white hover:bg-blue-800 font-semibold w-full"
                    >
                      {t('common.close')}
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {step !== '2fa' && (
          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-mode-btn"
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-blue-700 hover:text-blue-800 hover:underline font-medium"
            >
              {isLogin ? t('auth.noAccount') : t('auth.hasAccount')}
            </button>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;